import { Banner } from 'component/banner';
import { HorizBox, Pad, PadBox, Separator, WindowTitle } from 'component/basics';
import { colorBorderPrimary, colorSurfacePrimary, colorTextSecondary } from 'component/color';
import type { EditWidget } from 'component/comment';
import { DropdownField, FormField, SaveableTextField, Toggle } from 'component/form';
import { RichText } from 'component/richtext';
import { ConversationScreen } from 'component/screen';
import { CharacterCounter, Heading, UtilityText } from 'component/text';
import type { SpaceTemplate } from 'feature/editorial-dash/spaces';
import { getDefaultFeatureConfig } from 'feature/feature-helpers';
import { EDITORIALDASH_CAPABILITY } from 'feature/feature-roles';
import { HumanReviewsEverythingFeature } from 'feature/moderation/human-reviews-everything';
import { ContextEntryPointsFeature } from 'feature/question/context-entry-points';
import { RelatedConversationsEditScreenWidget } from 'feature/question/related-conversations';
import { TopTabFeature } from 'feature/ranking/top-tab';
import { useState } from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';
import type { QuestionConfig } from 'structure/question';
import { QuestionActionsOverlay } from 'structure/question/actions-overlay';
import { RichErrorMessage, useQuestionNameValidation } from 'structure/question/form';
import { Catcher } from 'system/catcher';
import { useHasCapability } from 'util/admin';
import { useDatastore, useGlobalProperty, useLogEvent, useStructureKey } from 'util/datastore';
import { useConfig, useMergedDefaultFeatures } from 'util/features';
import type { Feature, TLabel } from 'util/stdtypes';
import { t, undefS, undefTL } from 'util/stdtypes';
import { keysToMap } from 'util/util';

import { QUESTION_MAX_NAME_LENGTH } from './constants';
import { Question } from './view';

export function EditQuestionScreen() {
    const { topBanners, headerAboveInputWidgets, composerPreview, headerBelowInputWidgets, bottomRecircWidgets, tabs } =
        useConfig<QuestionConfig>();
    useLogEvent('question-edit');
    const name = useGlobalProperty('name');
    const s = EditQuestionScreenStyle;
    const hasCapability = useHasCapability(EDITORIALDASH_CAPABILITY);
    if (!hasCapability) {
        return (
            <Banner>
                <UtilityText label={t('You do not have access to this feature')} />
            </Banner>
        );
    }

    return (
        <View style={s.screen}>
            <WindowTitle formatParams={{ name }} label={t('Edit: {name}')} />
            <ConversationScreen>
                <Question
                    {...{ name, topBanners, headerAboveInputWidgets, composerPreview, headerBelowInputWidgets, bottomRecircWidgets, tabs }}
                />
            </ConversationScreen>
            <EditQuestionSidebar />
        </View>
    );
}

const EditQuestionScreenStyle = StyleSheet.create({
    screen: {
        flex: 1,
        flexDirection: 'row',
        backgroundColor: colorSurfacePrimary,
    },
});

export function EditQuestionSidebar() {
    const s = EditQuestionSidebarStyle;
    return (
        <View style={s.sidebar}>
            <ScrollView>
                <PadBox horiz={20} vert={20}>
                    <HorizBox center spread>
                        <Heading label={t('Edit')} type='large' weight='medium' />
                        <QuestionActionsOverlay />
                    </HorizBox>
                </PadBox>
                <Separator />
                <EditQuestionSidebarSection headingLabel={t('Thread header')}>
                    <EditQuestionName />
                </EditQuestionSidebarSection>
                <Separator />
                {/* <EditQuestionSidebarSection headingText={'Channel'}>
                <UtilityText type='small' text={'Channel picker will be here'} />
            </EditQuestionSidebarSection>
            <Separator /> */}
                <EditQuestionSidebarSection headingText={'Prosocial format'}>
                    <ProsocialFormatPicker />
                    <EditWidgets slot='prosocial-format-widget' />
                </EditQuestionSidebarSection>
                <Separator />
                <EditQuestionSidebarSection headingText={'Moderation'}>
                    <EditQuestionModerationSettings />
                </EditQuestionSidebarSection>
                <Separator />
                <EditQuestionSidebarSection headingText={'Additional features'}>
                    <EditQuestionAdditionalFeaturesSettings />
                </EditQuestionSidebarSection>
                <Separator />
                <EditQuestionSidebarSection headingText={'Add spaces'}>
                    <RelatedConversationsEditScreenWidget />
                </EditQuestionSidebarSection>
                <Separator />
                {/* <EditQuestionSidebarSection headingText={'Related Questions'}>
                <UtilityText type='small' text={'Related questions will be here'} />
            </EditQuestionSidebarSection> */}
            </ScrollView>
        </View>
    );
}

export function EditWidgets({ slot }: { slot: EditWidget['slot'] }) {
    const { editScreenWidgets } = useConfig<QuestionConfig>();
    const widgetsForSection = editScreenWidgets?.filter((widget) => widget.slot === slot) ?? [];
    return (
        <View>
            {widgetsForSection?.map((widget, i) => (
                <PadBox key={i} top={16}>
                    <Catcher>
                        <widget.component key={i} />
                    </Catcher>
                </PadBox>
            ))}
        </View>
    );
}

export function ProsocialFormatPicker() {
    const datastore = useDatastore();
    const format = useGlobalProperty('format');

    const { questionFormatTemplates } = useConfig<QuestionConfig>();

    const options = questionFormatTemplates?.map((template) => ({ key: template.key, label: template.label })) ?? [];
    const currentTemplate = questionFormatTemplates?.find((template) => template.key === format);
    return (
        <View>
            <FormField label={t('Prosocial format')} required>
                <DropdownField
                    onChange={async (value) => {
                        const newTemplate = questionFormatTemplates?.find((template) => template.key === value);
                        if (!newTemplate) {
                            return;
                        }
                        datastore.setGlobalProperty('format', value);
                        const featuresEnabledMap = keysToMap(newTemplate.featuresEnabled ?? [], () => true);
                        const featuresDisabledMap = keysToMap(newTemplate.featuresDisabled ?? [], () => false);
                        const features = { ...featuresEnabledMap, ...featuresDisabledMap };
                        datastore.setGlobalProperty('features', features);
                        await datastore.callServerAsync('questions', 'update', { questionKey: datastore.getInstanceKey(), format: value });
                    }}
                    options={options}
                    placeholder={t('Select a format')}
                    testID='format-selector'
                    value={format}
                />
            </FormField>
            <Pad size={16} />
            {currentTemplate?.editWidget && <currentTemplate.editWidget />}
        </View>
    );
}

const EditQuestionSidebarStyle = StyleSheet.create({
    sidebar: {
        width: 400,
        borderLeftWidth: 1,
        borderLeftColor: colorBorderPrimary,
    },
});

type EditQuestionSidebarSectionProps = {
    headingLabel?: TLabel;
    headingText?: string;
    children: React.ReactNode;
};

function EditQuestionSidebarSection({ headingLabel = undefTL, headingText = undefS, children }: EditQuestionSidebarSectionProps) {
    return (
        <PadBox horiz={20} vert={20}>
            <Heading label={headingLabel} text={headingText} type='small' weight='medium' />
            <Pad size={32} />
            {children}
        </PadBox>
    );
}

function EditQuestionName() {
    const datastore = useDatastore();
    const name = useGlobalProperty('name');
    const [tempValue, setTempValue] = useState(name);
    const [error, setError] = useState<JSX.Element | undefined>(undefined);

    const descriptionLabel = t('See the [editorial playbook]({url}) for guidance.');
    const playbookUrl = 'https://docs.google.com/document/d/10tennA0T63io5p3v1pnbL6FVdZ5_uHqr-NpF2KFPsVE/edit?usp=sharing';

    const handleValidation = useQuestionNameValidation(setError);

    return (
        <SaveableTextField
            accessoryActionWhenEditing={<CharacterCounter max={QUESTION_MAX_NAME_LENGTH} text={tempValue} />}
            description={<RichText color={colorTextSecondary} formatParams={{ url: playbookUrl }} label={descriptionLabel} />}
            error={error}
            label={t('Prompt')}
            onChange={(value) => {
                setTempValue(value);
                handleValidation(value);
            }}
            onSave={(value) => {
                datastore.setGlobalProperty('name', value);
            }}
            required
            value={name}
        />
    );
}

function EditQuestionModerationSettings() {
    return (
        <>
            <FeatureToggleField
                feature={HumanReviewsEverythingFeature}
                infoLabel={t('Require moderators to review all posts before they are published.')}
            />
            <FeatureToggleField
                feature={TopTabFeature}
                infoLabel={t('Allow publishing posts the AI moderation flagged as low quality but not harmful.')}
            />
        </>
    );
}

function EditQuestionAdditionalFeaturesSettings() {
    return (
        <>
            <FeatureToggleField
                feature={ContextEntryPointsFeature}
                infoLabel={t("Show the originating story below each user's comment.")}
            />
        </>
    );
}

type FeatureToggleFieldProps = {
    feature: Feature;
    infoLabel?: TLabel;
};

function useCurrentTemplate(): SpaceTemplate | undefined {
    const format = useGlobalProperty('format');
    const { questionFormatTemplates } = useConfig<QuestionConfig>();
    return questionFormatTemplates?.find((template) => template.key === format);
}

function FeatureToggleField({ feature, infoLabel = undefTL }: FeatureToggleFieldProps) {
    const structureKey = useStructureKey();
    const mergedDefaultFeatures = useMergedDefaultFeatures(structureKey);
    const defaultState = mergedDefaultFeatures[feature.key] ?? false;

    const features = useGlobalProperty('features');
    const enabled = features?.[feature.key] ?? defaultState;
    const datastore = useDatastore();
    const currentTemplate = useCurrentTemplate();

    const isFeatureHidden = currentTemplate?.featuresHidden?.includes(feature.key);
    if (isFeatureHidden) return null;

    if (feature.parentFeature && !features?.[feature.parentFeature]) return null;

    function onToggle() {
        const newFeatures = {
            ...features,
            [feature.key]: !enabled,
        };
        datastore.setGlobalProperty('features', newFeatures);
    }

    return <Toggle infoLabel={infoLabel} onChange={onToggle} spread text={feature.name} value={enabled || false} />;
}
